import React, { useState } from 'react';
import DealCard from './DealCard';
import { useDeals } from '../../context/DataContext';
import { fmtMoney } from '../../lib/format';
import { amountForView, dealTalentIds } from '../../lib/deals';
import { useRevenueView } from '../../context/RevenueViewContext';
import { Plus, Check, X } from 'lucide-react';
import { Input } from '../ui/input';

const InlineQuickAdd = ({ stageId, onDone, onCancel }) => {
  const { addDeal } = useDeals();
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await addDeal({ title: title.trim(), talent_id: null, brand_id: null, stage: stageId });
      onDone();
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-mint-300 rounded-lg p-2 bg-white shadow-sm">
      <Input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
        placeholder="deal title…"
        className="h-8 border-0 focus-visible:ring-0 px-1"
      />
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-slate-400">enter to add · esc to cancel</span>
        <div className="flex gap-1">
          <button onClick={onCancel} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
          <button onClick={submit} disabled={!title.trim() || saving} className="p-1 text-mint-600 hover:text-mint-700 disabled:opacity-40"><Check className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    </div>
  );
};

const KanbanColumn = ({ stage, deals, onCardClick, onDrop, dragging, setDragging, canAdd, onAdd }) => {
  const [over, setOver] = useState(false);
  const { view } = useRevenueView();
  const { talentMap } = useDeals();
  const total = deals.reduce((s, d) => {
    const primaryId = dealTalentIds(d)[0];
    return s + amountForView(d, view, primaryId ? talentMap[primaryId] : null);
  }, 0);

  return (
    <div
      className={`flex-1 min-w-0 flex flex-col rounded-xl border overflow-hidden ${over ? 'border-pink-400 shadow-lg shadow-pink-100' : 'border-slate-200'} transition-all bg-white`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(stage.id); }}
    >
      <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg,#d394ab 0%,#e9c2d5 60%,#d3c1e1 100%)' }} />
      <div className="px-3 py-3 flex items-center justify-between bg-gradient-to-b from-pink-50/60 to-white">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: stage.color }} />
          <h3 className="text-sm font-bold lowercase" style={{ color: '#8a2c5b' }}>{stage.label}</h3>
          <span className="text-xs font-semibold inline-flex items-center justify-center min-w-[20px] h-5 rounded-full px-1.5" style={{ background: '#d394ab', color: 'white' }}>{deals.length}</span>
        </div>
        {canAdd && (
          <button onClick={() => onAdd?.(stage.id)} className="p-1 rounded-md text-pink-500 hover:text-white hover:bg-pink-500 transition-colors" title="Add deal">
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="px-3 py-2 text-[11px] font-semibold border-b border-pink-100" style={{ color: '#a13a6b' }}>
        {fmtMoney(total)}
      </div>
      <div className="flex-1 p-2 space-y-1.5 overflow-y-auto bg-slate-50/40" style={{ minHeight: 200 }}>
        {deals.map((deal) => (
          <DealCard
            key={deal.id}
            deal={deal}
            onClick={() => onCardClick(deal)}
            dragProps={{
              draggable: true,
              onDragStart: () => setDragging(deal.id),
              onDragEnd: () => setDragging(null),
              style: { opacity: dragging === deal.id ? 0.5 : 1 },
            }}
          />
        ))}
        {deals.length === 0 && (
          <div className="text-center text-xs text-slate-400 py-6 border border-dashed border-slate-200 rounded-lg">
            drop deals here
          </div>
        )}
      </div>
    </div>
  );
};

const DealsKanban = ({ filteredDeals, onCardClick, onAdd, canAdd = true }) => {
  const { stages, moveDeal } = useDeals();
  const [dragging, setDragging] = useState(null);

  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex gap-3 w-full">
        {stages.map((stage) => (
          <KanbanColumn
            key={stage.id}
            stage={stage}
            deals={filteredDeals.filter((d) => d.stage === stage.id)}
            onCardClick={onCardClick}
            onDrop={(stageId) => { if (dragging) moveDeal(dragging, stageId); }}
            dragging={dragging}
            setDragging={setDragging}
            canAdd={canAdd && stage.id !== 'declined'}
            onAdd={onAdd}
          />
        ))}
      </div>
    </div>
  );
};

export default DealsKanban;
